import { countLetterInPhrase, getAvailableActions, GameState, VOWEL_COST } from './index';

export type JuiceCue = 'spin' | 'tick' | 'cash' | 'hit' | 'miss' | 'bankrupt' | 'lose' | 'solve' | 'win' | 'click';

export const HOW_TO_STORAGE_KEY = 'spin-solve-howto-seen';

export const HOW_TO_STEPS = [
  {
    title: 'Spin for cash',
    body: 'On your turn, pull the lever. The number is what each matching consonant is worth — not instant cash.',
  },
  {
    title: 'Tap a consonant',
    body: 'Use the on-screen letters. Hits pay and you keep your turn. Misses pass the turn.',
  },
  {
    title: 'Vowels & solve',
    body: 'Buy A E I O U for $250 from this round’s money. Solve the puzzle to bank what you have.',
  },
  {
    title: 'Win the night',
    body: 'BANKRUPT wipes round money only. Highest banked total after the last round wins.',
  },
] as const;

export const VOWEL_KEYS = ['a', 'e', 'i', 'o', 'u'] as const;
export const CONSONANT_ROWS: string[][] = [
  ['b', 'c', 'd', 'f', 'g', 'h', 'j'],
  ['k', 'l', 'm', 'n', 'p', 'q', 'r'],
  ['s', 't', 'v', 'w', 'x', 'y', 'z'],
];

export const SOLVE_ROWS: string[][] = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
];

export function formatMoney(amount: number): string {
  return `$${Math.max(0, Math.round(amount)).toLocaleString('en-US')}`;
}

export function phraseWords(phrase: string): string[] {
  return phrase.trim().split(/\s+/).filter(Boolean);
}

export function computeTileSize(phrase: string, maxWidth: number, maxHeight?: number): number {
  const words = phraseWords(phrase);
  const longest = Math.max(1, ...words.map((word) => word.length));
  const gap = 4;
  let size = Math.floor((maxWidth - (longest - 1) * gap) / longest);
  size = Math.max(20, Math.min(48, size));
  if (maxHeight && maxHeight > 48) {
    const rows = Math.max(1, words.length);
    const vGap = 6;
    const chrome = 26;
    const byHeight = Math.floor((maxHeight - chrome - (rows - 1) * vGap) / rows / 1.25);
    size = Math.max(18, Math.min(size, byHeight));
  }
  return size;
}

export type PromptTone = 'you' | 'wait' | 'round' | 'over';

export interface ActionPrompt {
  headline: string;
  detail: string;
  tone: PromptTone;
}

export function actionPrompt(state: GameState, playerId: string): ActionPrompt {
  const actions = getAvailableActions(state, playerId);
  const current = state.players[state.currentPlayerIndex];
  const name = current?.name || 'Someone';

  if (state.status === 'finished' || state.phase === 'game_over') {
    return { headline: 'Game over', detail: state.message, tone: 'over' };
  }
  if (state.phase === 'round_complete') {
    return { headline: 'Round complete', detail: state.message, tone: 'round' };
  }
  if (!actions.isMyTurn) {
    return {
      headline: `${name}'s turn`,
      detail: state.message || 'Hang tight — the board updates live.',
      tone: 'wait',
    };
  }
  if (actions.canGuessConsonant && state.currentSpinValue != null) {
    return {
      headline: 'Your turn — pick a consonant',
      detail: `Each one in the puzzle pays ${formatMoney(state.currentSpinValue)}.`,
      tone: 'you',
    };
  }
  if (actions.canBuyVowel) {
    return {
      headline: 'Your turn',
      detail: `Spin again, buy a vowel for ${formatMoney(VOWEL_COST)}, or solve.`,
      tone: 'you',
    };
  }
  return {
    headline: 'Your turn — spin or solve',
    detail: 'Pull the lever for cash, then call a consonant.',
    tone: 'you',
  };
}

export function playerDisplayName(user: { displayName: string | null; email: string | null } | null): string {
  if (!user) return 'Player';
  if (user.displayName?.trim()) return user.displayName.trim();
  const email = user.email || '';
  return email.split('@')[0] || 'Player';
}

export type OverlayKind = 'solve' | 'bankrupt' | 'win' | null;

export function juiceEventForTransition(prev: GameState | null, next: GameState): JuiceCue | null {
  if (!prev) return null;
  if ((next.status === 'finished' || next.phase === 'game_over') && prev.status !== 'finished') {
    return 'win';
  }
  if (next.phase === 'round_complete' && prev.phase !== 'round_complete') {
    return 'solve';
  }
  if (next.guessedLetters.length > prev.guessedLetters.length) {
    const letter = next.guessedLetters[next.guessedLetters.length - 1];
    return countLetterInPhrase(next.currentPhrase, letter) > 0 ? 'hit' : 'miss';
  }
  if (next.lastSpinAt && next.lastSpinAt !== prev.lastSpinAt && next.lastSpin) {
    if (next.lastSpin.specialValue === 'BANKRUPT') return 'bankrupt';
    if (next.lastSpin.specialValue === 'LOSE TURN') return 'lose';
    return 'cash';
  }
  return null;
}

export function overlayKindForTransition(prev: GameState | null, next: GameState): OverlayKind {
  const cue = juiceEventForTransition(prev, next);
  if (cue === 'win' || cue === 'solve' || cue === 'bankrupt') {
    return cue;
  }
  return null;
}

export function spinLandCue(specialValue: string | null | undefined): JuiceCue {
  if (specialValue === 'BANKRUPT') return 'bankrupt';
  if (specialValue === 'LOSE TURN') return 'lose';
  return 'cash';
}
